import type { Locale } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The console's dates, numbers and money, as the redesign prints them.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MOCKUP'S OWN FORMATTERS, PORTED, AND `Intl.DateTimeFormat` IS NOT ONE OF THEM.** Its
 * dates read *2024. márc 12.* — Intl's Hungarian medium date is *2024. márc. 12.*, with a period
 * after the month, and every date column in the console would differ from the drawing by one dot.
 * The month tables below are the mockup's, verbatim. Numbers and money do go through `Intl`, because
 * the mockup's do.
 *
 * ⚠️ **A wire timestamp is read as an instant and shown in the reader's own day.** A calendar date
 * on the wire (`YYYY-MM-DD`) is a day and is never shifted — `hard-won.md`'s driver rows are why
 * the two are kept apart.
 */

const MON = {
  hu: ['jan', 'febr', 'márc', 'ápr', 'máj', 'jún', 'júl', 'aug', 'szept', 'okt', 'nov', 'dec'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
} as const;

const MONL = {
  hu: [
    'január',
    'február',
    'március',
    'április',
    'május',
    'június',
    'július',
    'augusztus',
    'szeptember',
    'október',
    'november',
    'december',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
} as const;

const intlLocale = (locale: Locale): string => (locale === 'hu' ? 'hu-HU' : 'en-GB');

/** A calendar day from either a wire date or a wire timestamp. `null` for anything unreadable. */
function dayOf(value: string): { y: number; m: number; d: number } | null {
  const calendar = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (calendar !== null) {
    return { y: Number(calendar[1]), m: Number(calendar[2]) - 1, d: Number(calendar[3]) };
  }
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return { y: instant.getFullYear(), m: instant.getMonth(), d: instant.getDate() };
}

/** *2024. márc 12.* · *12 Mar 2024*; `long` spells the month: *2024. március 12.* */
export function formatDate(value: string | null, locale: Locale, long = false): string {
  if (value === null) return '–';
  const day = dayOf(value);
  if (day === null) return '–';
  if (locale === 'hu') {
    const month = long ? MONL.hu[day.m] : MON.hu[day.m];
    return `${String(day.y)}. ${month ?? ''} ${String(day.d)}.`;
  }
  const month = long ? MONL.en[day.m] : MON.en[day.m];
  return `${String(day.d)} ${month ?? ''} ${String(day.y)}`;
}

/**
 * *ma 08:12* · *tegnap 17:40* · *2026. okt 3. 09:15* — the mockup's `fdt`. Today and yesterday are
 * words, because *"ma"* is what somebody scanning a log is looking for.
 */
export function formatDateTime(
  value: string | null,
  locale: Locale,
  words: { readonly today: string; readonly yesterday: string },
): string {
  if (value === null) return '–';
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '–';
  const time = `${String(instant.getHours()).padStart(2, '0')}:${String(instant.getMinutes()).padStart(2, '0')}`;
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const same = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const day = same(instant, today)
    ? words.today
    : same(instant, yesterday)
      ? words.yesterday
      : formatDate(value, locale);
  return `${day} ${time}`;
}

/** Grouped the reader's way, rounded — the mockup's `nf`. */
export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(Math.round(value));
}

/**
 * *574 EUR* — whole units unless the amount has cents, the mockup's `money`.
 *
 * ⚠️ **The amount arrives as a STRING**, because every money value in this API is one: a
 * `numeric(14,2)` through a JavaScript number is where cents go to die. It is parsed only here, at
 * the last moment, for display.
 */
export function formatMoney(amount: string, currency: string, locale: Locale): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return amount;
  const cents = Math.round(value * 100) % 100 !== 0;
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value);
}

/** Today as a wire date in the reader's calendar — what the date picker marks as *today*. */
export function todayWire(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
